// @ts-nocheck

import fetch from 'isomorphic-fetch';
import { v4 as generateUUID } from 'uuid';
import { clean, sleep } from './utils';
import { getOneIdentityCorrelationId } from './selectors';

const HEADER_CONTENT_TYPE = 'Content-Type';
const HEADER_CORRELATION_ID = 'one-data-correlation-id';
const CONTENT_TYPE_JSON = 'application/json; charset=UTF-8';
const RETRY_DELAY = 1 * 1000;

const defaultCorrelationId = `LD-${generateUUID()}`;

const LOCATION_HEADER_NAME = 'Location';

/** @type {ErrorReporter} */
const ConsoleErrorReporter = process.env.NODE_ENV === 'development'
  // eslint-disable-next-line no-console -- used for debugging
  ? console.error // istanbul ignore next -- else return nothings
  : () => undefined;

const collectFetchReport = (init, request, response) => clean({
  ...init?.otherData,
  fetch: {
    ...init?.signal && { aborted: init.signal.aborted },
    request: {
      ...request.body && { bodyUsed: request.bodyUsed },
      cache: request.cache || init?.cache,
      credentials: request.credentials || init?.credentials,
      method: request.method,
      mode: request.mode || init?.mode,
      url: request.url,
    },
    ...response instanceof Response
      ? {
        response: {
          ...response.body && { bodyUsed: response.bodyUsed },
          ...response.headers && response.headers.has(LOCATION_HEADER_NAME) && {
            headers: {
              location: response.headers.get(LOCATION_HEADER_NAME),
            },
          },
          redirected: response.redirected,
          status: response.status,
          statusText: response.statusText,
          type: response.type,
          url: response.url,
        },
      }
      : {
        error: {
          message: response.message || String(response),
          ...response.stack
            && !response.stack.endsWith(response.message)
            && { stack: response.stack },
        },
      },
  },
});

/**
 * Create a fetch compatible function that reports errors.
 *
 * @param {CreateFetchWithReporterOptions} [options] Options used by the returned fetch client.
 * @returns {FetchWithReporter} A `fetch` compatible client that will report errors.
 */
export function createFetchWithReporter(options) {
  const {
    fetchClient = fetch,
    errorReporter: defaultErrorReporter = ConsoleErrorReporter,
  } = options || {};

  const fetchWithReporter = async (resource, init, state) => {
    const logSuccess = init?.logSuccess || false;
    const errorReporter = init?.errorReporter || defaultErrorReporter;
    const retryCount = init?.retryCount || 0;
    const retryDelay = init?.retryDelay || RETRY_DELAY;
    const globalCorrelationId = state && getOneIdentityCorrelationId(state);
    let attemptsRemaining = retryCount + 1;
    let error = null;

    const defaultInit = {
      headers: {
        [HEADER_CONTENT_TYPE]: CONTENT_TYPE_JSON,
        [HEADER_CORRELATION_ID]: globalCorrelationId || defaultCorrelationId,
      },
    };

    const finalInit = !init ? defaultInit : {
      ...defaultInit,
      ...init,
      headers: {
        ...defaultInit.headers,
        ...init.headers,
      },
    };

    const request = resource instanceof Request ? resource : new Request(resource, finalInit);

    while (attemptsRemaining > 0) {
      const startTime = Date.now();
      let roundTripResponseTime = null;

      try {
        /* eslint-disable-next-line no-await-in-loop -- used for retry */
        const response = await fetchClient(request, finalInit);
        roundTripResponseTime = Date.now() - startTime;
        const checkAndReportError = (data) => {
          if (!response.ok || logSuccess) {
            errorReporter(
              new Error(
                response.statusText || `Response is ${response.status || response.type || 'opaque'}`
              ),
              {
                ...data,
                roundTripResponseTime,
                startTime,
              }
            );
          }
        };

        if (init && init.awaitResolve) {
          /* eslint-disable-next-line no-await-in-loop -- used for retry */
          const json = await response.json();
          checkAndReportError({
            ...collectFetchReport(finalInit, request, response),
            json,
          });

          return { response, json };
        }

        checkAndReportError(collectFetchReport(finalInit, request, response));
        return response;
      } catch (err) {
        roundTripResponseTime = Date.now() - startTime;
        attemptsRemaining -= 1;
        error = err.stack ? err : new Error('Fetch error');
        const otherData = collectFetchReport(finalInit, request, error);

        if (attemptsRemaining === 0) {
          otherData.retryAttempts = retryCount;
          errorReporter(error, {
            ...otherData,
            roundTripResponseTime,
            startTime,
          });
        } else {
          /* eslint-disable-next-line no-await-in-loop -- used for retry */
          await sleep(retryDelay);
        }
      }
    }
    throw error;
  };

  return fetchWithReporter;
}

/**
 * Reports errors from global `fetch` to the console.
 */
export default createFetchWithReporter();

/**
 * Reports errors with optional metadata.
 *
 * @callback ErrorReporter
 * @param {Error} error The error to report.
 * @param {object} [otherData] Additional data to associate with the error report.
 */

/**
 * Fetch a resource from the network.
 *
 * @typedef {(input: RequestInfo, init?: RequestInit) => Promise<Response>} FetchClient
 */

/**
 * Default values used by the @{link FetchWithReporter} fetch client.
 *
 * @typedef CreateFetchWithReporterOptions
 * @property {FetchClient} [fetchClient] A `fetch` compatible client for fetching resources.
 * @property {ErrorReporter} [errorReporter] Reports errors returned from `fetch`.
 */

/**
 * Additional options passed to the {@link FetchWithReporter} fetch client.
 *
 * @typedef ReporterInit
 * @property {ErrorReporter} [errorReporter] Reports errors returned from `fetch`.
 * @property {object} [otherData] Additional data to associate with the error report.
 * @property {boolean} [awaitResolve] Await resolve before returning.
 */

/**
 * Additional settings to apply to a {@link FetchClient} that reports errors.
 *
 * @typedef {RequestInit & ReporterInit} RequestWithReporterInit
 */

/**
 * Redux state to get some options for the request
 *
 * @typedef {any} State
 */

/**
 * A fetch client that reports errors.
 *
 * @callback FetchWithReporter
 * @param {RequestInfo} resource The resource to fetch.
 * @param {RequestWithReporterInit} [init] The set of options to apply to the request.
 * @param {State} [state] redux state to get additional info for the request
 * @returns {Promise<Response>} A `Promise` that resolves to a `Response` object.
 */
